// Экран 5 — «Мой прогресс». Где ученик сейчас на карте.
//
// Чего здесь нет: прогноза балла. Он показывается раз в неделю и живёт в
// `progress.week` — ежедневная цель по результату повышает тревогу там, где
// стратегия ещё не освоена. Здесь только состояние: что открыто, что доведено
// до мастерства, что просело и сколько дней из двадцати одного были заняты.
//
// Данные приходят мутацией, а не запросом, и это про инвариант 4: обзору нужны
// все 226 строк состояния, а запрос в Convex — подписка по построению.
// Подписка на весь срез перезапускается на каждый ответ. Поэтому экран берёт
// снимок на открытии — и ничего не слушает. Кнопки «Обновить» с 20 сентября
// 2026 нет: экран монтируется заново на каждый вход, то есть снимок и так
// свежий, а изменить состояние карты, не уходя с него, нечем.
//
// Устройство экрана (20 сентября 2026): кнопка «назад» и заголовок, три числа и
// одна полоса — доведено, пройдено, всего; затем состав сегодняшнего набора
// свёрнутой секцией — он пришёл сюда со своей страницы. Разделы карты свёрнуты и
// раскрываются до навыков: девять строк вместо девяти полос с подписями.
// Список открытых навыков внизу — не витрина, а поиск: пятьдесят строк подряд
// никто не читает, а «найти вот этот навык и посмотреть его состояние» —
// настоящая задача.

import { useEffect, useMemo, useRef, useState } from "react";
import { api, useMutation, type DayPlan, type ProgressSnapshot } from "../api";
import { formatDay, plural } from "../lib/dates";
import { AimCard } from "../ui/AimCard";
import { IconChevron, IconReview } from "../ui/Icons";
import { SetCard } from "../ui/SetCard";
import { growth } from "../ui/Meter";

type OpenSkill = ProgressSnapshot["openSkills"][number];

/**
 * Группы открытых навыков для списка. Пороги не сравниваются здесь заново:
 * `passed`, `mastered` и `due` уже посчитаны сервером по порогам движка.
 * «Не пройдены», «Пройдены» и «Мастерство» не пересекаются и вместе дают все;
 * «Повторить» и «Без задач» режут список поперёк.
 */
const SKILL_FILTERS: readonly {
  id: string;
  label: string;
  test: (s: OpenSkill) => boolean;
}[] = [
  { id: "all", label: "Все", test: () => true },
  { id: "due", label: "Повторить", test: (s) => s.due },
  { id: "open", label: "Не пройдены", test: (s) => !s.passed },
  { id: "passed", label: "Пройдены", test: (s) => s.passed && !s.mastered },
  { id: "mastered", label: "Мастерство", test: (s) => s.mastered },
  { id: "untried", label: "Без задач", test: (s) => s.tried === 0 },
];
type CatalogSkill = ProgressSnapshot["catalog"][number];

/** Доля в целых процентах. Ноль знаменателя — пустой раздел, а не деление. */
function pct(value: number, scale: number): number {
  return scale === 0 ? 0 : Math.round((value / scale) * 100);
}

/** «2 сентября» — короче, чем в шапке дня: строк в списке семь. */
function shortDate(date: string): string {
  const [, m, d] = date.split("-").map(Number);
  const months = [
    "янв", "фев", "мар", "апр", "мая", "июн",
    "июл", "авг", "сен", "окт", "ноя", "дек",
  ];
  return `${d} ${months[(m ?? 1) - 1]}`;
}

/**
 * Полоса доли: залито — доведено до мастерства, светлее — пройдено, но ещё не
 * доведено, остаток дорожки — то, что впереди.
 *
 * Светлый слой считается по пройденному, а не по открытому: «открыт» значит
 * «очередь дошла», и полоса, растущая от открытия, показывала движение там,
 * где ученик ещё ничего не освоил.
 */
function Share({
  mastered,
  passed,
  total,
  wide,
}: {
  mastered: number;
  passed: number;
  total: number;
  /** Шире — для общей полосы в шапке; узкая стоит в строке раздела. */
  wide?: boolean;
}) {
  return (
    <div className={`bar bar--mini${wide === true ? " bar--mini-wide" : ""}`}>
      <div className="bar__base" style={{ width: `${pct(mastered, total)}%` }} />
      <div className="bar__delta" style={{ width: `${pct(passed - mastered, total)}%` }} />
    </div>
  );
}

/**
 * Строка навыка в раскрытом разделе и в поиске. Четыре величины в одном ряду:
 * имя, сколько задач решено верно, владение, долг по повторению.
 *
 * Закрытый навык приходит из `catalog`, где владения нет вовсе, — и стоит с
 * прочерком. Это факт данных, а не оформление: числа у закрытого навыка не
 * существует, и выдумывать ноль нельзя.
 */
function SkillLine({
  name,
  state,
  picked,
  onPick,
}: {
  name: string;
  /** Открытый навык или `null` — до него ещё не дошла очередь. */
  state: OpenSkill | null;
  picked: boolean;
  onPick: (skillId: string) => void;
}) {
  if (state === null) {
    return (
      <div className="skl skl--shut">
        <span className="skl__dot" />
        <span className="skl__name">{name}</span>
        <span className="skl__solved">не открыт</span>
        <span className="skl__level">—</span>
        <span className="skl__due skl__due--off" />
      </div>
    );
  }
  const tone = state.mastered ? "done" : state.passed ? "pass" : "open";
  return (
    <button
      type="button"
      className={`skl skl--${tone}${picked ? " skl--on" : ""}`}
      onClick={() => onPick(state.skillId)}
    >
      <span className="skl__dot" />
      <span className="skl__name">
        {name}
        {/*
          Помечается исключение, а не правило. Тег стоял на положительном
          признаке — и висел под 223 строками из 226: столько навыков карты
          входит во вступительное.
        */}
        {state.lyceum ? null : <span className="skl__note">не спросят</span>}
        {state.covered ? null : <span className="skl__note">задач нет</span>}
      </span>
      <span className="skl__solved">
        {state.tried === 0 ? "задач не было" : `решено ${state.correct} из ${state.tried}`}
      </span>
      {/*
        «≈» у числа без единой задачи по навыку. Самый дешёвый способ различить
        оценку и измерение: тильда читается до всякой легенды, а легенда
        объясняет её один раз на список.
      */}
      <span className={`skl__level${state.confidence === "none" ? " skl__level--guess" : ""}`}>
        {state.confidence === "none" ? "≈" : ""}
        {state.level}
      </span>
      <span
        className={`skl__due${state.due ? "" : " skl__due--off"}`}
        role="img"
        aria-label={state.due ? "пора повторить" : ""}
        title={state.due ? "пора повторить" : undefined}
      >
        <IconReview size={14} width={2.2} />
      </span>
    </button>
  );
}

/** Строка раздела в таблице: доля, числа и раскрытие до навыков. */
function SectionRow({
  section,
  skills,
  openById,
  expanded,
  onToggle,
  picked,
  onPick,
}: {
  section: ProgressSnapshot["sections"][number];
  skills: CatalogSkill[];
  openById: Map<string, OpenSkill>;
  expanded: boolean;
  onToggle: () => void;
  picked: string | null;
  onPick: (skillId: string) => void;
}) {
  return (
    <>
      <tr className="secs__row">
        <td>
          <button type="button" className="secs__name" aria-expanded={expanded} onClick={onToggle}>
            <span className="secs__chev">
              <IconChevron size={14} width={2.2} />
            </span>
            <span className="secs__label">{section.name}</span>
          </button>
        </td>
        <td className="secs__bar">
          <Share
            mastered={section.mastered}
            passed={section.passed}
            total={section.total}
          />
        </td>
        <td className="secs__num secs__num--faint">
          {section.passed}/{section.total}
        </td>
        <td className="secs__num">{pct(section.passed, section.total)}%</td>
        <td className="secs__num secs__num--done">{pct(section.mastered, section.total)}%</td>
      </tr>
      {expanded ? (
        <tr className="secs__sub">
          <td colSpan={5}>
            <div className="skls">
              {skills.map((c) => (
                <SkillLine
                  key={c.skillId}
                  name={c.name}
                  state={openById.get(c.skillId) ?? null}
                  picked={picked === c.skillId}
                  onPick={onPick}
                />
              ))}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function Progress({
  learnerKey,
  plan,
  onToday,
  onTestOut,
  onDrill,
  onSolve,
  onRestartDiagnostic,
}: {
  learnerKey: string;
  /**
   * Сегодняшний набор — для секции «Состав набора». Приходит готовым из App:
   * подписка на него уже открыта ради «Сегодня», и второй такой же здесь была
   * бы лишней. «История» и «Личные данные» ушли отсюда на «Сегодня» 20 сентября
   * 2026: оба экрана — переходы, и все переходы теперь в одном месте.
   */
  plan: DayPlan | null | undefined;
  onToday: () => void;
  /** Зачёт начат — экран уходит в задачи. */
  onTestOut: () => void;
  /** Тренажёр по выбранной теме: задачи подряд, ничего не двигающие. */
  onDrill: (skillId: string) => void;
  /** Задачи по теме добавлены в набор — экран уходит в первую из них. */
  onSolve: (itemKey: string) => void;
  /** Диагностика начата заново — экран уходит в неё. */
  onRestartDiagnostic: () => void;
}) {
  const snapshot = useMutation(api.progress.snapshot);
  const restart = useMutation(api.diagnostic.restart);
  const [data, setData] = useState<ProgressSnapshot | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const asked = useRef(false);
  // Выбранная тема живёт здесь, а не в карточке цели: её выбирают из трёх мест
  // — поиском по всем 226 навыкам, строкой в разделе и строкой в поиске по
  // открытым.
  const [picked, setPicked] = useState<string | null>(null);
  // Разделы свёрнуты по умолчанию: девять полос с подписями занимали экран
  // целиком, а вопрос «сколько я прошёл» на них не отвечал.
  const [showSections, setShowSections] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // Список открытых навыков свёрнут, пока не выбрана группа: полсотни строк
  // подряд при каждом визите не читают. Поиск идёт по выбранной группе, а без
  // неё — по всем открытым.
  const [skillFilter, setSkillFilter] = useState<string | null>(null);
  const aimRef = useRef<HTMLDivElement>(null);

  const load = () => {
    setFailed(null);
    void snapshot({ learnerKey })
      .then((d) => {
        setData(d);
        // Текущая цель подставляется только на первом снимке: пересъёмка после
        // смены цели не должна отбирать тему, которую ученик только что выбрал.
        setPicked((prev) => prev ?? d.aim?.skillId ?? null);
      })
      .catch((e: unknown) => setFailed(e instanceof Error ? e.message : String(e)));
  };

  /**
   * Нажатие на открытый навык. Задач по одному навыку подряд оно не даёт и не
   * должно: блочная практика противоречит перемешиванию (решение 3). Оно
   * наводит на тему две операции, которые в системе есть, — цель и зачёт.
   */
  const focusSkill = (skillId: string) => {
    setPicked(skillId);
    aimRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  useEffect(() => {
    if (asked.current) return;
    asked.current = true;
    // Один раз на открытие экрана: снимок, а не подписка. Ref, а не пустой
    // список зависимостей: StrictMode прогоняет эффект дважды, и снимок
    // собирался бы два раза на каждое открытие.
    load();
  }, []);

  // Навыки по разделам — из каталога, а не из открытых: раскрытый раздел
  // показывает и то, что ещё закрыто, иначе «освоено 3 из 22» не с чем
  // сверить. Порядок каталога — порядок карты, от опоры к надстройке.
  const byId = useMemo(
    () => new Map((data?.openSkills ?? []).map((s) => [s.skillId, s])),
    [data],
  );
  const bySection = useMemo(() => {
    const out = new Map<string, CatalogSkill[]>();
    for (const c of data?.catalog ?? []) {
      const list = out.get(c.section);
      if (list === undefined) out.set(c.section, [c]);
      else list.push(c);
    }
    return out;
  }, [data]);

  if (failed !== null) {
    return (
      <div className="cols cols--solo">
        <div className="col">
          <h1 className="h1">Обзор не собрался</h1>
          <p className="lede">{failed}</p>
          <div className="row row--actions" style={{ marginTop: 16 }}>
            <button className="btn btn--primary" onClick={load}>
              Ещё раз
            </button>
            <button className="link link--quiet" onClick={onToday}>
              К набору дня
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="stack">
        <div className="skeleton" style={{ width: 180 }} />
        <div className="skeleton" style={{ width: 320, height: 34 }} />
        <div className="card">
          <div className="skeleton" />
        </div>
      </div>
    );
  }

  const { skills, window: w } = data;
  const sectionName = new Map(data.sections.map((s) => [s.id, s.name]));
  // Недельный прирост — только если самый ранний снимок окна старше
  // сегодняшнего: иначе это то же число, что «сегодня», названное иначе.
  const week =
    data.progress.week !== null &&
    data.progress.weekFrom !== null &&
    data.progress.weekFrom !== data.date
      ? { value: data.progress.week, from: data.progress.weekFrom }
      : null;

  const needle = query.trim().toLowerCase();
  const filter = SKILL_FILTERS.find((f) => f.id === (skillFilter ?? "all")) ?? SKILL_FILTERS[0]!;
  const inFilter = data.openSkills.filter(filter.test);
  const found =
    needle === "" ? inFilter : inFilter.filter((s) => s.name.toLowerCase().includes(needle));
  const listOpen = skillFilter !== null || needle !== "";

  return (
    <div className="cols">
      <div className="col">
        <div>
          {/*
            Ни заголовка «Прогресс по карте», ни кнопки «назад»: и то и другое
            стоит в шапке приложения (24 сентября 2026). Экран начинается
            числами — за ними сюда и приходят.
          */}
          {/*
            Три числа одной шкалы: доведено ⊂ пройдено ⊂ карта. «Открыто»
            отвечает на вопрос «до чего дошла очередь», а спрашивают «сколько я
            прошёл»; раньше четыре числа на экране отвечали на него по-разному,
            и ни одно не было названо главным.
          */}
          <div className="tally">
            <div className="tally__cell">
              <div className="tally__n tally__n--done">{skills.mastered}</div>
              <div className="tally__l">доведено</div>
            </div>
            <div className="tally__cell">
              <div className="tally__n">{skills.passed}</div>
              <div className="tally__l">пройдено</div>
            </div>
            <div className="tally__cell">
              <div className="tally__n tally__n--rest">{skills.total}</div>
              <div className="tally__l">всего на карте</div>
            </div>
          </div>
          <div className="sum">
            <Share
              mastered={skills.mastered}
              passed={skills.passed}
              total={skills.total}
              wide
            />
            <div className="sum__pcts">
              <span className="sum__pct">
                <b>{pct(skills.passed, skills.total)}%</b> пройдено
              </span>
              <span className="sum__pct sum__pct--done">
                <b>{pct(skills.mastered, skills.total)}%</b> мастерство
              </span>
              <span className="sum__pct">
                к повторению <b>{skills.due}</b>
              </span>
            </div>
          </div>
          <p className="lede" style={{ marginTop: 12 }}>
            {plan?.examTitle ?? "Экзамен"} · осталось <span className="num">{data.daysToExam}</span>{" "}
            {plural(data.daysToExam, "день", "дня", "дней")}
            {data.progress.today === null ? null : (
              <>
                {" · сегодня "}
                <span className="num">{growth(data.progress.today)}</span>
              </>
            )}
            {week === null ? null : (
              <>
                {" · с "}
                {formatDay(week.from)} <span className="num">{growth(week.value)}</span>
              </>
            )}
          </p>
        </div>

        {/*
          Состав сегодняшнего набора — первой секцией: это единственное, что на
          экране про сегодня, а всё остальное про карту целиком. Свёрнут до двух
          строк, темы — после раскрытия. Разбор — в src/ui/SetCard.tsx.
        */}
        <SetCard plan={plan} skills={data.openSkills} />

        {/*
          Пересдача — не настройка и не кнопка: тихая ссылка, и её нет, как
          только ученик решил первую задачу. Дальше переразмещают зачётом,
          который ничего не стирает.
        */}
        {data.canRestartDiagnostic ? (
          <div className="row row--actions">
            <button
              className="link link--quiet"
              onClick={() => {
                void restart({ learnerKey })
                  .then(onRestartDiagnostic)
                  .catch((e: unknown) =>
                    setFailed(e instanceof Error ? e.message : String(e)),
                  );
              }}
            >
              Пройти диагностику заново
            </button>
          </div>
        ) : null}

        <div className="card">
          <div className="card__head">
            <div className="card__label">Разделы карты · {data.sections.length}</div>
            <button
              type="button"
              className="btn btn--icon"
              aria-expanded={showSections}
              onClick={() => setShowSections((v) => !v)}
            >
              <span className="secs__chev">
                <IconChevron size={14} width={2.2} />
              </span>
              {showSections ? "Свернуть" : "Развернуть"}
            </button>
          </div>
          {showSections ? (
            <>
              {/*
                Ключ к полосе, а не украшение: тёмный слой «доведено» невидим,
                пока доведённых ноль, и код «тёмное/светлое» выучить не на чем.
              */}
              <div className="faint" style={{ marginTop: 10, lineHeight: 1.5 }}>
                В полосе: <span className="key key--done" /> доведено до мастерства,{" "}
                <span className="key key--open" /> пройдено, остальное — впереди. Проценты
                считаются от всех навыков раздела. Раздел раскрывается до списка навыков.
              </div>
              <table className="secs">
                <thead>
                  <tr>
                    <th>Раздел</th>
                    <th />
                    <th>навыков</th>
                    <th>пройдено</th>
                    <th>мастерство</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sections.map((s) => (
                      <SectionRow
                        key={s.id}
                        section={s}
                        skills={bySection.get(s.id) ?? []}
                        openById={byId}
                        expanded={openSection === s.id}
                        onToggle={() => setOpenSection((v) => (v === s.id ? null : s.id))}
                        picked={picked}
                        onPick={focusSkill}
                      />
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div className="faint" style={{ marginTop: 10 }}>
              Девять разделов карты: сколько навыков пройдено в каждом и какие именно.
            </div>
          )}
        </div>

        <div className="card">
          <div className="card__label">Открытые навыки · {data.openSkills.length}</div>
          {/*
            Число и знак «≈» — две разные величины. Владение приходит четырьмя
            путями, и три из них — вывод модели, а не решённые задачи:
            размещение диагностикой, кредит с соседних навыков по «задействует»
            и замыкание сданного зачёта. Измерено на 30 искусственных учениках:
            среди навыков, которые размещение объявило известными, известны на
            самом деле 84% — каждое шестое такое число неверно.
          */}
          <div className="faint" style={{ marginTop: 8, lineHeight: 1.5 }}>
            Справа у навыка — владение, 0–100; пройден с 75, мастерство с 90. «≈» значит,
            что задач по навыку не было и число выведено по карте. Иконка{" "}
            <span className="skl__due skl__due--inline">
              <IconReview size={13} width={2.2} />
            </span>{" "}
            — память просела, пора повторить. Нажатие на навык выбирает его темой в
            карточке «Карта: цель и зачёт».
          </div>
          {data.openSkills.length === 0 ? (
            <div className="faint" style={{ marginTop: 12 }}>
              Пока ни одного: навыки открываются из набора дня.
            </div>
          ) : (
            <>
              <div className="chips" style={{ marginTop: 12 }}>
                {SKILL_FILTERS.map((f) => {
                  const count = data.openSkills.filter(f.test).length;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      className="chip"
                      aria-pressed={skillFilter === f.id}
                      disabled={count === 0}
                      onClick={() => setSkillFilter((v) => (v === f.id ? null : f.id))}
                    >
                      {f.label} · <span className="num">{count}</span>
                    </button>
                  );
                })}
              </div>
              <input
                className="field field--inline find"
                type="search"
                placeholder={
                  skillFilter === null || skillFilter === "all"
                    ? "найти открытый навык"
                    : `найти в группе «${filter.label}»`
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              {listOpen ? (
                <>
                  <div className="skls skls--flat">
                    {found.map((s) => (
                      <SkillLine
                        key={s.skillId}
                        name={s.name}
                        state={s}
                        picked={picked === s.skillId}
                        onPick={focusSkill}
                      />
                    ))}
                  </div>
                  <div className="faint" style={{ marginTop: 10 }}>
                    {found.length === 0 ? (
                      "Ничего не нашлось."
                    ) : needle === "" ? (
                      <>
                        {filter.label} · <span className="num">{found.length}</span> из{" "}
                        <span className="num">{data.openSkills.length}</span>
                      </>
                    ) : (
                      <>
                        Найдено <span className="num">{found.length}</span> из{" "}
                        <span className="num">{inFilter.length}</span>
                        {found.length === 1 && found[0] !== undefined
                          ? ` · раздел «${sectionName.get(found[0].section) ?? found[0].section}»`
                          : ""}
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className="faint" style={{ marginTop: 10 }}>
                  Список свёрнут: выберите группу или найдите навык по имени.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="col">
        <div className="card">
          <div className="card__label">Занятия</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginTop: 12 }}>
            <span className="num">{w.days}</span> {plural(w.days, "день", "дня", "дней")} из{" "}
            <span className="num">{w.of}</span>
          </div>
          <details className="explain explain--inline">
            <summary>
              <span className="explain__mark" aria-hidden="true">?</span>
              Как считается
            </summary>
            <div className="explain__body muted">
              Считаются дни, когда решена хотя бы одна задача, за последние{" "}
              <span className="num">{w.of}</span> дней. Цель — заняться в{" "}
              <span className="num">{w.target}</span> из них. Пропущенный день ничего не
              обнуляет: каждый день просто входит в подсчёт на 21 день, а затем
              уступает место новому.
            </div>
          </details>
          {data.days.length > 0 ? (
            <>
              <hr className="hr" />
              <div className="stack stack--tight">
                {data.days.map((d) => (
                  <div key={d.date} className="move__head">
                    <span className="move__name">{shortDate(d.date)}</span>
                    <span className="move__value num">
                      {d.correct}/{d.tasks} · {d.minutes} мин
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>

        {/*
          Это справка, а не ежедневный показатель. В раскрытом виде карточка
          занимала верх правой колонки при каждом визите, хотя читают её один-два
          раза. Нативный details оставляет объяснение рядом с экраном, но по
          умолчанию показывает только одну строку.
        */}
        <div className="card card--explain">
          <details className="explain">
            <summary>
              <span className="explain__mark" aria-hidden="true">?</span>
              <span>Как устроен прогресс</span>
              <span className="explain__action">Открыть</span>
            </summary>
            <div className="stack stack--tight explain__body">
              <div>
                <div style={{ fontWeight: 600 }}>Карта</div>
                <div className="muted">
                  Навыки, связанные «что на чём стоит». Навык открывается,
                  когда пройдено то, на чём он держится. Закрытый — не запрет, а
                  ещё не дошедшая очередь. Почти вся карта — темы экзамена;
                  навыки вне цели подготовки помечены «не спросят».
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>Набор дня</div>
                <div className="muted">
                  Собирает система: сперва просевшие навыки, потом один новый на
                  выбор, потом задача уровня экзамена. Темы идут
                  вперемешку нарочно — выбирать метод учатся только так.
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>Владение</div>
                <div className="muted">
                  Число 0–100 у каждого навыка. Растёт от верных ответов и
                  садится со временем без повторения — поэтому навык
                  возвращается. 90 и выше — доведён до мастерства.
                </div>
              </div>
              <div>
                <div style={{ fontWeight: 600 }}>Что решаешь сам</div>
                <div className="muted">
                  Какой новый навык взять из предложенных, какую тему сделать
                  целью, какую сдать зачётом и сколько задач добавить сверх
                  набора.
                </div>
              </div>
            </div>
          </details>
        </div>

        <div ref={aimRef}>
          <AimCard
            learnerKey={learnerKey}
            catalog={data.catalog}
            aim={data.aim}
            sections={data.sections}
            openSkills={data.openSkills}
            picked={picked}
            onPick={setPicked}
            onChanged={load}
            onTestOut={onTestOut}
            onDrill={onDrill}
            onPractice={onSolve}
          />
        </div>
      </div>
    </div>
  );
}
